import { signal, Signal, computed, isDevMode } from '@angular/core';

/**
 * Clase base abstracta para servicios de estado.
 * Provee un signal tipado, selección por key, y logging automático en dev mode.
 *
 * @example
 * ```typescript
 * interface IMyState { count: number; name: string; }
 *
 * @Injectable({ providedIn: 'root' })
 * export class MyStateService extends StateService<IMyState> {
 *   protected readonly stateName = 'MyState';
 *   constructor() {
 *     super({ count: 0, name: '' });
 *   }
 * }
 * ```
 */
export abstract class StateService<T> {
  /** Nombre del state para identificarlo en los logs de consola. */
  protected abstract readonly stateName: string;

  readonly state: ReturnType<typeof signal<T>>;

  constructor(initialState: T) {
    this.state = signal(initialState);
  }

  /**
   * Selecciona una propiedad del estado como signal de solo lectura.
   * @param key - Key del estado a seleccionar
   */
  public select<K extends keyof T>(key: K): Signal<T[K]> {
    return computed(() => this.state()[key]);
  }

  /**
   * Actualiza parcialmente el estado.
   * En dev mode, loguea el estado resultante en consola.
   * @param partialState - Propiedades a actualizar
   */
  public setState(partialState: Partial<T>): void {
    this.state.update((current) => {
      const newState = { ...current, ...partialState };

      if (isDevMode()) {
        console.log(`%c[${this.stateName}]`, 'color: #7c3aed; font-weight: bold;', newState);
      }

      return newState;
    });
  }
}
